import {
  ComparisonResult,
  QueryColumn,
  StatisticsQuery,
  StatisticsResult,
} from '../types/tool-result.types';

export type ChartKind = 'trend' | 'ranking' | 'compare';

/** Normalised row: the chart never reads backend keys, only these three. */
export interface ChartRow {
  label: string;
  value: number;
  value2?: number;
}

export interface ChartCard {
  kind: ChartKind;
  rows: ChartRow[];
  seriesLabel: string;
  series2Label?: string;
}

/** Above this a bar chart is unreadable; the table view still shows everything. */
export const MAX_CHART_ROWS = 36;

const TOOL_KINDS: Record<string, ChartKind> = {
  sales_trend: 'trend',
  sales_ranking: 'ranking',
  sales_compare_periods: 'compare',
  sales_compare_entities: 'compare',
};

/** The metric fields ai-mcp's METRIC_MAP can produce, as a reader sees them. */
const METRIC_LABELS: Record<string, string> = {
  net: 'Umsatz',
  gross: 'Bruttoumsatz',
  contribution_margin: 'Deckungsbeitrag',
  cost: 'Wareneinsatz',
  discount_value: 'Rabatt',
  quantity: 'Menge',
};

/**
 * Builds a chart from a tool result, or returns `null` when the result cannot
 * be charted — unknown tool, missing keys, or too many rows. `null` is not an
 * error: the bubble then renders the answer's markdown table only.
 */
export function chartCardFromToolResult(
  toolName: string,
  content: string | undefined
): ChartCard | null {
  const kind = TOOL_KINDS[toolName];
  if (!kind || !content) return null;

  const parsed = parseJson(content);
  if (!parsed || !Array.isArray(parsed['data'])) return null;

  const card =
    kind === 'compare'
      ? compareCard(parsed as unknown as ComparisonResult)
      : singleSeriesCard(kind, parsed as unknown as StatisticsResult);

  if (!card || card.rows.length === 0 || card.rows.length > MAX_CHART_ROWS) return null;
  return card;
}

/** `sales_ranking` / `sales_trend`: one value column, keys derived from the query. */
function singleSeriesCard(
  kind: Exclude<ChartKind, 'compare'>,
  result: StatisticsResult
): ChartCard | null {
  const columns = result.query?.columns ?? [];
  const value = columns.find((c) => c.usage === 'AGGREGATE');
  const groups = columns.filter((c) => c.usage === 'GROUP');
  // The time bucket carries the date function; a ranking's last GROUP column is
  // the readable one (e.g. customer.name after customer.number).
  const label = kind === 'trend' ? groups.find((c) => c.function) : lastOf(groups);
  if (!value || !label) return null;
  // A trend grouped by an entity as well produces several rows per bucket —
  // that is a multi-series chart, which this card does not render.
  if (kind === 'trend' && groups.length > 1) return null;

  const labelKey = columnKey(label);

  const rows: ChartRow[] = [];
  for (const row of result.data) {
    const amount = toNumber(readAggregate(row, value));
    if (amount === null) return null;
    rows.push({ label: String(readCell(row, labelKey) ?? ''), value: amount });
  }

  return { kind, rows, seriesLabel: metricLabel(value.name) };
}

/** `sales_compare_periods` / `sales_compare_entities`: two value columns per row. */
function compareCard(result: ComparisonResult): ChartCard | null {
  const labels = result.column_labels ?? {};
  const period1 = Object.keys(labels).find((k) => k.includes('_period1:'));
  const period2 = Object.keys(labels).find((k) => k.includes('_period2:'));
  if (!period1 || !period2) return null;

  const groupColumn = lastOf(
    (result.query?.columns ?? []).filter((c) => c.usage === 'GROUP')
  );
  if (!groupColumn) return null;

  const rows: ChartRow[] = [];
  for (const row of result.data) {
    const first = toNumber(row[period1]);
    const second = toNumber(row[period2]);
    if (first === null || second === null) return null;
    rows.push({
      label: String(row[groupColumn.name] ?? ''),
      value: first,
      value2: second,
    });
  }

  return {
    kind: 'compare',
    rows,
    seriesLabel: labels[period1],
    series2Label: labels[period2],
  };
}

/**
 * The response key of a column: what was applied to the field is appended to
 * its name — `statistics.net:sum`, `statistics.document_date:month`.
 */
export function columnKey(column: QueryColumn): string {
  if (column.function) return `${column.name}:${column.function}`;
  if (column.agg) return `${column.name}:${column.agg}`;
  return column.name;
}

/**
 * Rows come back flat with dotted keys (`customer.name`, `statistics.net:sum`) —
 * ai-mcp flattens every result. The nested lookup is the fallback for the raw
 * statistics-service shape.
 */
function readCell(row: Record<string, unknown>, key: string): unknown {
  if (row[key] !== undefined) return row[key];
  const separator = key.indexOf('.');
  if (separator === -1) return undefined;
  const bucket = row[key.slice(0, separator)];
  if (!bucket || typeof bucket !== 'object') return undefined;
  return (bucket as Record<string, unknown>)[key.slice(separator + 1)];
}

/**
 * `avg` is computed in ai-mcp after the query ran, so the column still says
 * `agg: 'sum'` while the row carries `<field>:avg` — fall back to whatever
 * aggregate of the field the row actually has.
 */
function readAggregate(row: Record<string, unknown>, column: QueryColumn): unknown {
  const direct = readCell(row, columnKey(column));
  if (direct !== undefined) return direct;
  const prefix = `${column.name}:`;
  const key = Object.keys(row).find((k) => k.startsWith(prefix));
  return key === undefined ? undefined : row[key];
}

/** `statistics.net` → `Umsatz`; an unmapped field keeps its bare name. */
function metricLabel(name: string): string {
  const separator = name.lastIndexOf('.');
  const field = separator === -1 ? name : name.slice(separator + 1);
  return METRIC_LABELS[field] ?? field;
}

function lastOf<T>(items: T[]): T | undefined {
  return items.length > 0 ? items[items.length - 1] : undefined;
}

function toNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
}

function parseJson(content: string): Record<string, unknown> | null {
  try {
    const parsed = JSON.parse(content) as unknown;
    return parsed && typeof parsed === 'object'
      ? (parsed as Record<string, unknown>)
      : null;
  } catch {
    return null;
  }
}

export type { StatisticsQuery };
